import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Avatar, Typography, CircularProgress, Grid, Tooltip, useMediaQuery, Paper, Chip, TextField, Stack, InputAdornment } from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Business, LocalHospital, School, Store, Science, CardTravel,
  HomeWork, AttachMoney, AccountBalance, Gavel, Computer,
  Search, Groups,
} from '@mui/icons-material';

const industries = [
  { label: 'IT', icon: <Computer />, value: 'it', color: '#546e7a' },
  { label: 'Insurance', icon: <Business />, value: 'insurance', color: '#1976d2' },
  { label: 'Healthcare', icon: <LocalHospital />, value: 'healthcare', color: '#e53935' },
  { label: 'Education', icon: <School />, value: 'education', color: '#8e24aa' },
  { label: 'Research', icon: <Science />, value: 'research', color: '#43a047' },
  { label: 'Travel', icon: <CardTravel />, value: 'travel', color: '#00acc1' },
  { label: 'Real Estate', icon: <HomeWork />, value: 'real-estate', color: '#6d4c41' },
  { label: 'Finance', icon: <AttachMoney />, value: 'finance', color: '#2e7d32' },
  { label: 'Banking', icon: <AccountBalance />, value: 'banking', color: '#3949ab' },
  { label: 'Legal', icon: <Gavel />, value: 'legal', color: '#5e35b1' },
  { label: 'Small Industries', icon: <Store />, value: 'small-industries', color: '#f57c00' },
  
];

const Companies = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const industryParam = queryParams.get('industry');

  const [selectedIndustry, setSelectedIndustry] = useState(industryParam || 'it');
  
  const [rolesData, setRolesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    fetchRolesData(selectedIndustry);
    // Fetch whenever the selected industry changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustry]);

  const fetchRolesData = async (industry) => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/companies?industry=${industry}`);
      const groupedRoles = groupRolesByCompany(response.data);
      setRolesData(groupedRoles);
    } catch (error) {
      console.error('Error fetching roles data', error);
      setRolesData([]);
    } finally {
      setLoading(false);
    }
  };

  const groupRolesByCompany = (data) => {
    const grouped = {};

    data.forEach((item) => {
      item.roles.forEach((role) => {
        if (!grouped[item.role]) {
          grouped[item.role] = [];
        }
        const existingCompany = grouped[item.role].find((company) => company.companyname === role.companyname);

        if (existingCompany) {
          existingCompany.employeeCount += role.employeeCount;
        } else {
          grouped[item.role].push({ companyname: role.companyname?role.companyname:'Non-disclosed', employeeCount: role.employeeCount });
        }
      });
    });

    return grouped;
  };

  const selectedIndustryMeta = industries.find((industry) => industry.value === selectedIndustry) || industries[0];
  const roleEntries = useMemo(() => Object.entries(rolesData), [rolesData]);
  const filteredRoleEntries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return roleEntries;

    return roleEntries
      .map(([role, companies]) => {
        const roleMatches = role.toLowerCase().includes(normalizedSearch);
        const matchedCompanies = companies.filter((company) =>
          company.companyname.toLowerCase().includes(normalizedSearch)
        );
        return roleMatches ? [role, companies] : [role, matchedCompanies];
      })
      .filter(([role, companies]) => role.toLowerCase().includes(normalizedSearch) || companies.length > 0);
  }, [roleEntries, searchTerm]);

  const companyCount = roleEntries.reduce((total, [, companies]) => total + companies.length, 0);
  const employeeCount = roleEntries.reduce(
    (total, [, companies]) => total + companies.reduce((sum, company) => sum + Number(company.employeeCount || 0), 0),
    0
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        mx: { xs: -2, sm: -3, md: -4 },
        my: { xs: -2, sm: -3, md: -4 },
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 12% 12%, rgba(20, 184, 166, 0.16), transparent 30%), radial-gradient(circle at 88% 4%, rgba(37, 99, 235, 0.13), transparent 28%), linear-gradient(135deg, #f8fafc 0%, #eef7f6 48%, #f8fafc 100%)',
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.45fr 1fr' },
            gap: 2.5,
            alignItems: 'stretch',
            mb: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 4,
              border: '1px solid rgba(15, 118, 110, 0.14)',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Chip
              label="Udyog Connect Network"
              sx={{
                mb: 2,
                bgcolor: '#e6fffb',
                color: '#0f766e',
                border: '1px solid #99f6e4',
                fontWeight: 800,
              }}
            />
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              sx={{ color: '#0f172a', fontWeight: 900, lineHeight: 1.08, mb: 1.5 }}
            >
              Explore companies and people across industry roles.
            </Typography>
            <Typography sx={{ color: '#475569', maxWidth: 760, lineHeight: 1.7 }}>
              Select an industry to understand where community members are connected, which roles
              are represented, and which companies can help you discover pathways and contacts.
            </Typography>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', lg: '1fr' },
              gap: 1.5,
            }}
          >
            {[
              { label: 'Selected industry', value: selectedIndustryMeta.label },
              { label: 'Role groups', value: roleEntries.length },
              { label: 'People mapped', value: employeeCount },
            ].map((stat) => (
              <Paper
                key={stat.label}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(15, 118, 110, 0.12)',
                  bgcolor: 'rgba(255, 255, 255, 0.82)',
                  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ color: '#0f172a', fontWeight: 900, mt: 0.75 }}>
                  {stat.value}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2, md: 3 },
            borderRadius: 4,
            border: '1px solid rgba(15, 118, 110, 0.13)',
            boxShadow: '0 22px 70px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 900 }}>
                Industry directory
              </Typography>
              <Typography sx={{ color: '#64748b', mt: 0.5 }}>
                Pick an industry, then search roles or companies in the selected network.
              </Typography>
            </Box>
            <TextField
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search role or company"
              sx={{ minWidth: { xs: '100%', md: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 1.5,
              mb: 3,
              pb: 1,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 999 },
              flexWrap: 'nowrap',
              width: '100%',
            }}
          >
            {industries.map((industry) => (
              <motion.div
                key={industry.value}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                transition={{ duration: 0.2 }}
              >
                <Paper
                  elevation={0}
                  onClick={() => {
                    setSelectedIndustry(industry.value);
                    setSearchTerm('');
                  }}
                  sx={{
                    width: { xs: 94, sm: 118 },
                    minHeight: { xs: 112, sm: 126 },
                    p: 1.5,
                    borderRadius: 3,
                    cursor: 'pointer',
                    border: selectedIndustry === industry.value ? `2px solid ${industry.color}` : '1px solid #e2e8f0',
                    bgcolor: selectedIndustry === industry.value ? '#f0fdfa' : '#ffffff',
                    boxShadow: selectedIndustry === industry.value ? '0 16px 36px rgba(15, 118, 110, 0.14)' : '0 8px 24px rgba(15, 23, 42, 0.05)',
                    transition: 'all 0.2s ease',
                    textAlign: 'center',
                  }}
                >
                  <Tooltip title={industry.label}>
                    <Avatar
                      sx={{
                        width: isMobile ? 50 : 58,
                        height: isMobile ? 50 : 58,
                        mx: 'auto',
                        backgroundColor: industry.color,
                        color: '#fff',
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16)',
                      }}
                    >
                      {industry.icon}
                    </Avatar>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    fontWeight="800"
                    textAlign="center"
                    sx={{
                      mt: 1,
                      color: '#0f172a',
                      fontSize: isMobile ? '0.75rem' : '0.86rem',
                      lineHeight: 1.25,
                    }}
                  >
                    {industry.label}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
              <CircularProgress size={44} sx={{ color: '#0f766e' }} />
            </Box>
          ) : roleEntries.length > 0 ? (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                <Chip icon={<Groups />} label={`${companyCount} company entries`} sx={{ bgcolor: '#f8fafc', fontWeight: 800 }} />
                <Chip label={`${filteredRoleEntries.length} matching role groups`} sx={{ bgcolor: '#f8fafc', fontWeight: 800 }} />
              </Stack>
              {filteredRoleEntries.length > 0 ? (
                <Grid container spacing={2.5}>
                  {filteredRoleEntries.map(([role, companies], index) => (
                    <Grid item xs={12} sm={6} md={4} key={role}>
                      <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            minHeight: 210,
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 12px 34px rgba(15, 23, 42, 0.06)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                              transform: { xs: 'none', md: 'translateY(-4px)' },
                              boxShadow: '0 18px 44px rgba(15, 23, 42, 0.1)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 900, lineHeight: 1.25 }}>
                              {role}
                            </Typography>
                            <Chip size="small" label={companies.length} sx={{ bgcolor: '#e6fffb', color: '#0f766e', fontWeight: 900 }} />
                          </Box>
                          <Stack spacing={1}>
                            {companies.map((company, idx) => (
                              <Box
                                key={`${company.companyname}-${idx}`}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  gap: 1.5,
                                  p: 1,
                                  borderRadius: 2,
                                  bgcolor: '#f8fafc',
                                  border: '1px solid #eef2f7',
                                }}
                              >
                                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 700 }}>
                                  {company.companyname}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#0f766e', fontWeight: 900 }}>
                                  {company.employeeCount}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Paper>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box textAlign="center" sx={{ py: 8, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                  <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 900 }}>
                    No matching companies found.
                  </Typography>
                  <Typography sx={{ color: '#64748b', mt: 0.75 }}>
                    Try another role or company search within {selectedIndustryMeta.label}.
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" sx={{ py: 8, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 900 }}>
                No companies found for this industry.
              </Typography>
              <Typography sx={{ color: '#64748b', mt: 0.75 }}>
                Select a different industry to explore the wider Udyog Connect network.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Companies;
